import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster, toast } from 'react-hot-toast';
import { Header } from './components/Header';
import { Landing } from './components/Landing';
import { Dashboard } from './components/Dashboard';
import { Account } from './components/Account';
import Preview from './components/Preview';
import { AuthModal } from './components/auth/AuthModal';
import { supabase } from './lib/supabase';
import { Session } from '@supabase/supabase-js';
import { scrapeJobDescription } from './lib/jobScraper';
import CoinsPage from './pages/CoinsPage';
import MobileNavBar from './components/MobileNavBar';
import PaymentConfirmation from './pages/PaymentConfirmation';
import PaymentReturn from './pages/PaymentReturn';
import PaykuReturn from './pages/PaykuReturn';
import PaykuCancel from './pages/PaykuCancel';
import PaykuWebhooks from './pages/paykuwebhooks';
import VerifyPayment from './pages/VerifyPayment';
import VerifyPaymentPage from './pages/VerifyPaymentPage';
import TransactionsPage from './pages/TransactionsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import CookiesPolicyPage from './pages/CookiesPolicyPage';
import TermsAndConditionsPage from './pages/TermsAndConditionsPage';
import CookieConsent from './components/CookieConsent';
import HotmartSuccessPage from './pages/hotmart/success';
import HotmartCancelPage from './pages/hotmart/cancel';

// Importamos las nuevas páginas
import { Features } from './components/Features';
import { Pricing } from './components/Pricing';
import { Testimonials } from './components/Testimonials';
import { Contact } from './components/Contact';
import { Footer } from './components/Footer';

// Importamos los componentes de administración
import { AdminLogin, AdminDashboard, AdminRoute } from './components/admin';
import { FloatingMenu } from './components/FloatingMenu';
import { MyCVsPage } from './pages/MyCVsPage';

function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [jobUrl, setJobUrl] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [coinsRefreshTrigger, setCoinsRefreshTrigger] = useState(0);
  const [showCookieConsent, setShowCookieConsent] = useState(false);
  const analysisTimer = React.useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    console.log('Iniciando App...');

    const initSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;
        setSession(data.session);
      } catch (error) {
        console.error('Error al inicializar sesión:', error);
      } finally {
        setLoading(false);
      }
    };

    initSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    // Escuchar el evento de actualización de monedas
    const handleCoinsUpdated = () => {
      setCoinsRefreshTrigger(prev => prev + 1);
    };

    window.addEventListener('coinsUpdated', handleCoinsUpdated);

    // Verificar si ya se aceptaron las cookies
    const cookiesAccepted = localStorage.getItem('cookies-accepted');
    setShowCookieConsent(cookiesAccepted === null); // Mostrar si no hay preferencia guardada

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('coinsUpdated', handleCoinsUpdated);
    };
  }, []);

  const handleAcceptCookies = () => {
    localStorage.setItem('cookies-accepted', 'true');
    setShowCookieConsent(false);

    // Aquí podrías activar las cookies de análisis/marketing que requieran consentimiento
    toast.success('Has aceptado las cookies', {
      duration: 3000,
      position: 'bottom-center',
      style: {
        background: '#1e293b',
        color: '#fff',
        borderRadius: '8px',
      },
      icon: '🍪',
    });
  };

  const handleRejectCookies = () => {
    localStorage.setItem('cookies-accepted', 'false');
    setShowCookieConsent(false);

    // Aquí podrías desactivar las cookies no esenciales
    toast('Cookies no esenciales rechazadas', {
      duration: 3000,
      position: 'bottom-center',
      style: {
        background: '#1e293b',
        color: '#fff',
        borderRadius: '8px',
      },
      icon: '🛑',
    });
  };

  if (loading) {
    return <div>Cargando...</div>;
  }

  return (
    <Router>
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-black flex flex-col">
        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-blue-500/20 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-purple-500/20 rounded-full blur-[80px]" />
        </div>

        <div className="relative z-10 flex flex-col min-h-screen">
          <Toaster position="top-right" />
          <Header
            session={session}
            onAuthClick={() => setIsAuthModalOpen(true)}
            coinsRefreshTrigger={coinsRefreshTrigger}
          />
          <main className="w-full pb-16 sm:pb-0 pt-12 flex-grow">
            <Routes>
              <Route path="/" element={!session ? <Landing onAuthClick={() => setIsAuthModalOpen(true)} /> : <Navigate to="/dashboard" />} />
              <Route path="/dashboard" element={session ? <Dashboard session={session} /> : <Navigate to="/" />} />
              <Route path="/account" element={session ? <Account /> : <Navigate to="/" />} />
              <Route path="/my-cvs" element={session ? <MyCVsPage /> : <Navigate to="/" />} />
              <Route path="/preview" element={session ? <Preview /> : <Navigate to="/" />} />
              <Route path="/coins" element={session ? <CoinsPage /> : <Navigate to="/" />} />
              <Route path="/transactions" element={session ? <TransactionsPage session={session} /> : <Navigate to="/" />} />
              <Route path="/payment/confirmation" element={<PaymentConfirmation />} />
              <Route path="/payment/return" element={<PaymentReturn />} />
              <Route path="/payment-confirmation" element={<PaymentConfirmation />} />
              <Route path="/payku-return" element={<PaykuReturn />} />
              <Route path="/payku-cancel" element={<PaykuCancel />} />
              <Route path="/paykuwebhooks" element={<PaykuWebhooks />} />
              <Route path="/verify-payment" element={<VerifyPaymentPage />} />
              <Route path="/verify-payment/status" element={<VerifyPayment />} />

              {/* Hotmart Routes */}
              <Route path="/hotmart/success" element={<HotmartSuccessPage />} />
              <Route path="/hotmart/cancel" element={<HotmartCancelPage />} />

              {/* Nuevas rutas */}
              <Route path="/features" element={<Features />} />
              <Route path="/pricing" element={<Pricing session={session} onAuthClick={() => setIsAuthModalOpen(true)} />} />
              <Route path="/testimonials" element={<Testimonials />} />
              <Route path="/contact" element={<Contact />} />

              {/* Rutas legales */}
              <Route path="/terms" element={<TermsAndConditionsPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/cookies" element={<CookiesPolicyPage />} />

              {/* Rutas de administración */}
              <Route path="/admin" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              } />
            </Routes>
          </main>

          {/* Footer que aparece en todas las páginas */}
          <Footer />

          {session && <MobileNavBar />}
          {session && <FloatingMenu session={session} />}

          {/* Banner de consentimiento de cookies */}
          {showCookieConsent && (
            <CookieConsent
              onAccept={handleAcceptCookies}
              onReject={handleRejectCookies}
            />
          )}
        </div>

        <AuthModal
          isOpen={isAuthModalOpen}
          onClose={() => setIsAuthModalOpen(false)}
        />
      </div>
    </Router>
  );
}

export default App;